import axios from "axios";
import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../hooks/useAuth";
import { programsAPI, adminAPI, imageLibraryAPI, challengesAPI } from "../services/api";
import { PageWrapper, Card, Button, Input, Textarea, MembershipBadge } from "../components";
import { 
  ArrowLeft, BookOpen, Users, BarChart3, Crown, Check,
  Plus, Pencil, Trash2, ExternalLink, ChevronUp, ChevronDown, Image, Trophy,
  Search, Filter, ToggleLeft, ToggleRight, Clock, X, Mic, Sparkles, UserPlus, Copy,
  Activity, AlertCircle, AlertTriangle, RefreshCw, Library, Share2, HardDrive, Wand2, MessageSquare, CreditCard, Eye
} from "lucide-react";

export const AdminLayout = ({ children }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  if (user?.role !== "admin") {
    return <Navigate to="/" replace />;
  }

  const tabs = [
    { path: "/admin", label: "Overview", icon: BarChart3 },
    { path: "/admin/programs", label: "Programs", icon: BookOpen },
    { path: "/admin/challenges", label: "Challenges", icon: Trophy },
    { path: "/admin/users", label: "Users", icon: Users },
    { path: "/admin/guides", label: "Guide Answers", icon: Users },
    { path: "/admin/costs", label: "Cost per User", icon: Users },
    { path: "/admin/library-growth", label: "Library Growth", icon: BarChart3 },
    { path: "/admin/talking-clips", label: "Talking Clips", icon: Users },
    { path: "/admin/affiliates", label: "Affiliates", icon: UserPlus },
    { path: "/admin/mlm", label: "Commissions", icon: BarChart3 },
    { path: "/admin/teams", label: "Teams", icon: Users },
    { path: "/admin/images", label: "Images", icon: Image },
    { path: "/admin/journey-images", label: "Journey Images", icon: Image },
    { path: "/admin/audio", label: "Audio", icon: Mic },
    { path: "/admin/voice-coverage", label: "Voice Coverage", icon: Sparkles },
    { path: "/admin/voice-bank-recorder", label: "Voice Bank", icon: Mic },
    { path: "/admin/audio/resync", label: "Audio Sync", icon: Mic },
    { path: "/admin/gift-priming", label: "Gift Priming", icon: Sparkles },
    { path: "/admin/golf/scripts", label: "Golf Scripts", icon: Mic },
    { path: "/admin/golf/signatures", label: "Golf Signatures", icon: Mic },
    { path: "/admin/concierge", label: "Concierge Welcome", icon: Mic },
    { path: "/admin/grant-access", label: "Grant Access", icon: Sparkles },
    { path: "/admin/influencers", label: "Influencer Links", icon: Users },
    { path: "/admin/golf-partners", label: "Golf Partners", icon: Users },
    { path: "/admin/library-restore", label: "Restore to My Library", icon: Library },
    { path: "/admin/payments", label: "Payments", icon: CreditCard },
    { path: "/soulumo/demo", label: "Preview the Demo", icon: Eye },
    { path: "/admin/merge-accounts", label: "Merge Accounts", icon: Users },
    { path: "/admin/voice-clone-interest", label: "Voice Clone Signals", icon: Mic },
    { path: "/admin/beta-feedback", label: "Feedback & Testimonials", icon: MessageSquare },
    { path: "/admin/journey-diagnostics", label: "Journey Render Diagnostics", icon: AlertCircle },
    { path: "/admin/stuck-resets", label: "Stuck Resets", icon: Sparkles },
    { path: "/admin/bulk-invite", label: "Bulk Invite", icon: Mic },
    { path: "/admin/exports", label: "Exports Catalogue", icon: Sparkles },
    { path: "/admin/excelior-training", label: "Excelior", icon: Sparkles },
  ];

  return (
    <PageWrapper showNav={true}>
      <div className="p-6 pt-16 sm:pt-6 max-w-4xl mx-auto space-y-6">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h1 className="font-heading text-2xl text-[#292524]">Admin Dashboard</h1>
            <p className="text-sm text-[#78716C]">Manage your content</p>
          </div>
          <Button variant="ghost" className="whitespace-nowrap" onClick={() => navigate("/")} data-testid="exit-admin">
            <ArrowLeft size={18} /> Exit Admin
          </Button>
        </div>

        <div className="flex gap-2 border-b border-[#E6E2DD] overflow-x-auto whitespace-nowrap [scrollbar-width:thin]">
          {tabs.map(({ path, label, icon: Icon }) => (
            <button
              key={path}
              onClick={() => navigate(path)}
              className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors ${
                location.pathname === path
                  ? "border-[#4A6741] text-[#4A6741]"
                  : "border-transparent text-[#78716C] hover:text-[#292524]"
              }`}
              data-testid={`admin-tab-${label.toLowerCase()}`}
            >
              <Icon size={18} />
              {label}
            </button>
          ))}
        </div>

        {children}
      </div>
    </PageWrapper>
  );
};

// Launch tools, one tap from the top of Overview (the tab row scrolls sideways on phones).
const SHORTCUTS = [
  ["/soulumo/demo", "Preview the demo", Eye],
  ["/admin/golf-partners?tab=funnel", "Demo results", Activity],
  ["/admin/golf-partners?tab=board", "Partner leaderboard", Users],
  ["/admin/golf-partners", "Golf partner applications", Users],
  ["/admin/library-restore", "Restore to my library", Library],
  ["/admin/payments", "Payments (Stripe / Paddle)", CreditCard],
  ["/admin/merge-accounts", "Merge accounts", Users],
];
const LaunchShortcuts = () => {
  const navigate = useNavigate();
  return (
    <div className="mb-6" data-testid="admin-launch-shortcuts">
      <p className="mb-2 text-xs uppercase tracking-widest text-[#78716C]">Launch tools</p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {SHORTCUTS.map(([to, label, Icon]) => (
          <button key={to} type="button" onClick={() => navigate(to)} data-testid={`admin-shortcut-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            className="flex items-center gap-2 rounded-xl border border-[#E7E5E4] bg-white px-3 py-3 text-left text-sm text-[#292524] transition-colors hover:bg-[#F5F5F4]">
            <Icon size={16} className="shrink-0 text-[#4A6741]" /> {label}
          </button>
        ))}
      </div>
    </div>
  );
};


export const AdminOverview = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [health, setHealth] = useState(null);
  const [healthLoading, setHealthLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await adminAPI.getStats();
        setStats(res.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  // System health — refresh every 30 s while admin is on the overview.
  useEffect(() => {
    let timer;
    const tick = async () => {
      try {
        const res = await adminAPI.getSystemHealth();
        setHealth(res.data);
      } catch (e) {
        setHealth({ ok: false, error: e?.message || "unreachable", checks: [] });
      } finally {
        setHealthLoading(false);
      }
    };
    tick();
    timer = setInterval(tick, 30000);
    return () => clearInterval(timer);
  }, []);

  if (loading) {
    return <div className="text-center py-8 text-[#78716C]">Loading...</div>;
  }

  const statCards = [
    { label: "Total Users", value: stats?.users || 0, icon: Users },
    { label: "Paid Members", value: stats?.paid_members || 0, icon: Crown },
    { label: "Programs", value: stats?.programs || 0, icon: BookOpen },
    { label: "Completions", value: stats?.completions || 0, icon: Check },
  ];

  return (
    <AdminLayout>
      <LaunchShortcuts />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {statCards.map(({ label, value, icon: Icon }) => (
          <Card key={label} className="p-4 text-center">
            <Icon className="mx-auto mb-2 text-[#4A6741]" size={24} />
            <p className="font-heading text-2xl text-[#292524]">{value}</p>
            <p className="text-sm text-[#78716C]">{label}</p>
          </Card>
        ))}
      </div>

      {/* LLM Universal Key Health — top of overview so it's the first thing admin sees */}
      <LlmHealthPanel />

      {/* System Health Panel — auto-refreshes every 30 s */}
      <SystemHealthPanel health={health} loading={healthLoading} />

      {/* Library usage counter — refreshes every 60 s */}
      <LibraryUsagePanel />

      {/* Voice-Bank recording sheet download (Feb 2026 · Michael's
          weekend recording session). Bearer-authorised blob download
          so iOS Safari doesn't 401 on a raw URL paste. */}
      <VoiceBankSheetPanel />
    </AdminLayout>
  );
};

// ─── Voice-Bank Recording Sheet download panel ────────────────────
const VoiceBankSheetPanel = () => {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);
  const API = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const t = localStorage.getItem("reclaim_token");
    if (!t) return;
    fetch(`${API}/api/admin/canonical-affirmations/summary`, {
      headers: { Authorization: `Bearer ${t}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d && setSummary(d))
      .catch(() => {});
  }, [API]);

  const download = async (fmt) => {
    setBusy(fmt);
    setError(null);
    try {
      const t = localStorage.getItem("reclaim_token");
      const r = await fetch(
        `${API}/api/admin/canonical-affirmations/export.${fmt}`,
        { headers: { Authorization: `Bearer ${t}` } },
      );
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `soulumo_recording_sheet.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.message || "Download failed");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-[#EAE0CC] p-6 mb-6" data-testid="voice-bank-sheet-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div>
          <h3 className="text-[#4A6741] text-lg font-semibold">🎙 Voice-Bank Recording Sheet</h3>
          <p className="text-sm text-[#7a6a4a] mt-1">
            Every canonical affirmation, grouped by theme and section.
            Read each aloud into your voice bank — those lines then
            play back in your own voice, free of ElevenLabs charge.
          </p>
        </div>
        {summary && (
          <div className="text-right text-xs text-[#7a6a4a] leading-tight">
            <div className="text-2xl font-semibold text-[#4A6741]">{summary.total_lines}</div>
            lines · {summary.themes_covered} themes
          </div>
        )}
      </div>
      <div className="flex items-center gap-3 flex-wrap">
        <button
          onClick={() => navigate("/admin/voice-bank-recorder")}
          data-testid="voice-bank-open-recorder"
          className="px-4 py-2 rounded-full text-sm bg-[#A88A4C] text-white hover:opacity-90"
        >
          🎙 Record in-app (recommended)
        </button>
        <button
          onClick={() => download("md")}
          disabled={busy}
          data-testid="voice-bank-download-md"
          className="px-4 py-2 rounded-full text-sm bg-[#4A6741] text-white hover:opacity-90 disabled:opacity-50"
        >
          {busy === "md" ? "Preparing…" : "📄 Download Markdown sheet"}
        </button>
        <button
          onClick={() => download("csv")}
          disabled={busy}
          data-testid="voice-bank-download-csv"
          className="px-4 py-2 rounded-full text-sm border border-[#4A6741] text-[#4A6741] hover:bg-[#f4efe1] disabled:opacity-50"
        >
          {busy === "csv" ? "Preparing…" : "📊 Download CSV"}
        </button>
        {error && <span className="text-sm text-red-600">{error}</span>}
      </div>
    </div>
  );
};

// ─── LLM Universal Key Health Panel ───────────────────────────────
// Surfaces the latest known state of the Emergent Universal LLM Key:
// last success / last failure timestamps, captured budget figures (if
// the key reported a "Budget has been exceeded" error), and a one-tap
// "Top up" link to the Emergent dashboard. Admin can also fire a live
// probe (1-prompt-token call) to verify the key is currently healthy.
const LlmHealthPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [probing, setProbing] = useState(false);

  const fetchHealth = useCallback(async () => {
    try {
      const r = await adminAPI.getLlmHealth();
      setData(r.data);
    } catch (e) {
      setData({ status: "unknown", error: e?.message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchHealth(); }, [fetchHealth]);

  const probe = async () => {
    setProbing(true);
    try {
      const r = await adminAPI.probeLlmHealth();
      setData(r.data);
    } catch (e) {
      setData({ status: "error", error: e?.message });
    } finally {
      setProbing(false);
    }
  };

  const status = data?.status || "unknown";
  const pct = data?.percent_used;
  const palette =
    status === "healthy" ? STATUS_COLOR.ok :
    status === "exhausted" ? STATUS_COLOR.down :
    status === "error" ? STATUS_COLOR.warn :
    STATUS_COLOR.warn;
  const statusLabel =
    status === "healthy" ? "Healthy" :
    status === "exhausted" ? "Budget exhausted" :
    status === "error" ? "Recent error" :
    "Unknown";

  const fmt = (iso) => {
    if (!iso) return "—";
    const t = new Date(iso);
    if (Number.isNaN(t.getTime())) return "—";
    const diff = (Date.now() - t.getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return t.toLocaleString();
  };

  return (
    <Card className="p-5 mt-2" data-testid="admin-llm-health">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Sparkles size={18} className={palette.text} />
          <h3 className="font-heading text-lg text-[#292524]">Universal LLM Key</h3>
          {!loading && (
            <span
              className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium border ${palette.bg} ${palette.border} ${palette.text}`}
              data-testid="admin-llm-health-pill"
            >
              <span className={`w-1.5 h-1.5 rounded-full ${palette.dot}`} />
              {statusLabel}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={probe}
            disabled={probing}
            className="px-3 py-1.5 rounded-full text-xs font-medium border border-[#D7D3CE] text-[#292524] hover:bg-[#F5F4F2] disabled:opacity-50"
            data-testid="admin-llm-health-probe"
          >
            {probing ? "Probing…" : "Run live probe"}
          </button>
          <a
            href={data?.top_up_url || "https://app.emergent.sh/profile"}
            target="_blank"
            rel="noreferrer"
            className="px-3 py-1.5 rounded-full text-xs font-medium bg-[#7A6126] text-white hover:bg-[#5F4B1D]"
            data-testid="admin-llm-health-topup"
          >
            Top up now →
          </a>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-[#78716C]">Loading…</p>
      ) : (
        <>
          {/* Budget bar — only shown if we have captured numbers */}
          {typeof pct === "number" && (
            <div className="mb-4" data-testid="admin-llm-health-budget">
              <div className="flex justify-between text-xs text-[#78716C] mb-1">
                <span>Budget used</span>
                <span>
                  ${data?.current_cost?.toFixed(2)} of ${data?.max_budget?.toFixed(2)} ·
                  {" "}{pct}%
                </span>
              </div>
              <div className="w-full h-2 rounded-full bg-[#E6E2DD] overflow-hidden">
                <div
                  className={`h-full ${pct >= 95 ? "bg-rose-500" : pct >= 80 ? "bg-amber-500" : "bg-emerald-500"}`}
                  style={{ width: `${Math.min(100, pct)}%` }}
                />
              </div>
              {data?.budget_captured_at && (
                <p className="text-[10px] text-[#A8A29E] mt-1">
                  Captured from a 402/exhausted response · {fmt(data.budget_captured_at)}
                </p>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-xs uppercase tracking-wider text-[#A8A29E] mb-0.5">Last success</p>
              <p className="text-[#292524]">{fmt(data?.last_success_at)}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-[#A8A29E] mb-0.5">Last failure</p>
              <p className="text-[#292524]">{fmt(data?.last_failure_at)}</p>
              {data?.last_failure_source && (
                <p className="text-xs text-[#78716C] mt-0.5">
                  from <code className="bg-[#F5F4F2] px-1 rounded">{data.last_failure_source}</code>
                </p>
              )}
            </div>
          </div>

          {data?.last_failure_message && status !== "healthy" && (
            <p className="mt-3 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2 font-mono">
              {data.last_failure_message}
            </p>
          )}
        </>
      )}
    </Card>
  );
};

// ─── System Health Panel ───────────────────────────────────────────
// At-a-glance status of the platform's moving parts. The probes that
// matter: MongoDB, disk space, audio library, immersion video, user
// library API. Each check returns ok | warn | down. Auto-refreshes
// every 30 s — admin spots silent service crashes in one glance.
const STATUS_COLOR = {
  ok:   { bg: "bg-emerald-50",  border: "border-emerald-200", text: "text-emerald-700", dot: "bg-emerald-500" },
  warn: { bg: "bg-amber-50",    border: "border-amber-200",   text: "text-amber-700",   dot: "bg-amber-500"   },
  down: { bg: "bg-rose-50",     border: "border-rose-200",    text: "text-rose-700",    dot: "bg-rose-500"    },
};

const SystemHealthPanel = ({ health, loading }) => {
  const overall = health?.ok ? "ok" : (health?.checks?.some((c) => c.status === "down") ? "down" : "warn");
  const palette = STATUS_COLOR[overall] || STATUS_COLOR.warn;
  const checked = health?.checked_at ? new Date(health.checked_at) : null;

  return (
    <Card className="p-5 mt-2" data-testid="admin-system-health">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Activity size={18} className={palette.text} />
          <h3 className="font-heading text-lg text-[#292524]">System Health</h3>
          {!loading && health && (
            <span
              className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium border ${palette.bg} ${palette.border} ${palette.text}`}
              data-testid="admin-health-pill"
            >
              <span className={`w-1.5 h-1.5 rounded-full ${palette.dot}`} />
              {overall === "ok" ? "Healthy" : overall === "warn" ? "Degraded" : "Down"}
            </span>
          )}
        </div>
        {checked && (
          <p className="text-xs text-[#A8A29E]">
            checked {checked.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
          </p>
        )}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-[#78716C]">
          <RefreshCw size={14} className="animate-spin" /> Probing system…
        </div>
      ) : !health?.checks?.length ? (
        <div className="flex items-center gap-2 text-sm text-rose-700">
          <AlertCircle size={14} /> Health endpoint unreachable
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {health.checks.map((c) => {
            const p = STATUS_COLOR[c.status] || STATUS_COLOR.warn;
            const Icon = c.status === "ok" ? Check : (c.status === "warn" ? AlertTriangle : AlertCircle);
            return (
              <div
                key={c.id}
                className={`flex items-start gap-3 p-3 rounded-lg border ${p.bg} ${p.border}`}
                data-testid={`admin-health-${c.id}`}
              >
                <Icon size={16} className={`mt-0.5 ${p.text} flex-shrink-0`} />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-[#292524]">{c.label}</p>
                  <p className={`text-xs ${p.text} truncate`} title={c.detail}>{c.detail}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
};

// ─── Library Usage Panel ──────────────────────────────────────────
// Michael asked for cost visibility on the growing personal audio
// library. Reads the honest, measurable counters from Mongo — no
// third-party billing calls — and highlights the one number that
// actually costs money as it grows (AI renders this month → ElevenLabs).
const LibraryUsagePanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetch = useCallback(async () => {
    try {
      const r = await adminAPI.getLibraryUsage();
      setData(r.data);
    } catch (e) {
      setData({ error: e?.response?.data?.detail || e?.message });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetch();
    const t = setInterval(fetch, 60000); // refresh every 60s
    return () => clearInterval(t);
  }, [fetch]);

  const refresh = () => {
    setRefreshing(true);
    fetch();
  };

  if (loading) {
    return (
      <Card className="p-5 mt-2" data-testid="admin-library-usage">
        <p className="text-sm text-[#78716C]">Loading library usage…</p>
      </Card>
    );
  }

  if (data?.error) {
    return (
      <Card className="p-5 mt-2" data-testid="admin-library-usage">
        <p className="text-sm text-red-600">Couldn&apos;t load library usage: {data.error}</p>
      </Card>
    );
  }

  const stats = [
    { label: "Items in library",       value: data.total_items,             icon: Library,   accent: "text-amber-600" },
    { label: "Curators",               value: data.unique_users,            icon: Users,     accent: "text-emerald-600",
      hint: data.avg_items_per_user ? `avg ${data.avg_items_per_user} per user` : null },
    { label: "AI renders · this month", value: data.ai_renders_month,       icon: Wand2,     accent: "text-rose-600",
      hint: `${data.ai_renders_lifetime.toLocaleString()} lifetime` },
    { label: "Shared to Reclaim",      value: data.public_shares_on,        icon: Share2,    accent: "text-sky-600",
      hint: data.public_share_opens_total ? `${data.public_share_opens_total.toLocaleString()} opens` : null },
    { label: "Storage estimate",       value: `${data.storage_estimate_mb} MB`, icon: HardDrive, accent: "text-purple-600",
      hint: "500 KB / item avg" },
  ];

  // Top 6 sources by count
  const topSources = Object.entries(data.by_source || {})
    .sort((a, b) => b[1] - a[1])
    .filter(([, v]) => v > 0)
    .slice(0, 6);

  return (
    <Card className="p-5 mt-2" data-testid="admin-library-usage">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Library size={18} className="text-amber-600" />
          <h3 className="font-heading text-lg text-[#292524]">Library usage</h3>
        </div>
        <button
          onClick={refresh}
          disabled={refreshing}
          className="text-xs text-[#78716C] hover:text-[#292524] flex items-center gap-1 disabled:opacity-50"
          data-testid="admin-library-usage-refresh"
        >
          <RefreshCw size={12} className={refreshing ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {stats.map(({ label, value, icon: Icon, accent, hint }) => (
          <div
            key={label}
            className="rounded-xl bg-white/70 border border-[#E7E5E4] p-3"
            data-testid={`library-stat-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
          >
            <div className="flex items-center gap-1.5">
              <Icon size={14} className={accent} />
              <p className="text-[10px] tracking-[0.14em] uppercase text-[#78716C]">{label}</p>
            </div>
            <p className="mt-1.5 font-heading text-xl text-[#292524]">
              {typeof value === "number" ? value.toLocaleString() : value}
            </p>
            {hint && <p className="text-[10px] text-[#A8A29E] mt-0.5">{hint}</p>}
          </div>
        ))}
      </div>

      {topSources.length > 0 && (
        <div className="mt-4 pt-4 border-t border-[#E7E5E4]">
          <p className="text-[10px] tracking-[0.18em] uppercase text-[#78716C] mb-2">Top sources</p>
          <div className="flex flex-wrap gap-1.5">
            {topSources.map(([source, count]) => (
              <span
                key={source}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#FAF6EF] border border-[#E7E5E4] text-xs text-[#57534E]"
                data-testid={`library-source-${source}`}
              >
                <span className="font-medium text-[#292524]">{count.toLocaleString()}</span>
                <span>{source.replace(/_/g, ' ')}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      <p className="mt-3 text-[10px] text-[#A8A29E] leading-relaxed">
        {data.storage_estimate_note} · Generated {new Date(data.generated_at).toLocaleTimeString()}.
      </p>
    </Card>
  );
};

const _AdminOverviewEnd = () => null; // keep accidental edits below this marker safe
// eslint-disable-next-line no-unused-vars
const _placeholder = _AdminOverviewEnd;

export const AdminPrograms = () => {
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingProgram, setEditingProgram] = useState(null);
  const [showProgramForm, setShowProgramForm] = useState(false);
  const navigate = useNavigate();

  const fetchPrograms = useCallback(async () => {
    try {
      const res = await programsAPI.getAll();
      setPrograms(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPrograms();
  }, [fetchPrograms]);

  const handleDeleteProgram = async (programId) => {
    if (!window.confirm("Delete this program and all its content?")) return;
    try {
      await adminAPI.deleteProgram(programId);
      fetchPrograms();
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h2 className="font-heading text-xl text-[#292524]">Programs</h2>
          <Button onClick={() => { setEditingProgram(null); setShowProgramForm(true); }} data-testid="add-program-btn">
            <Plus size={18} /> Add Program
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-8 text-[#78716C]">Loading...</div>
        ) : (
          <div className="space-y-3">
            {programs.map((program) => (
              <Card key={program.id} className="p-4">
                <div className="flex items-center gap-4">
                  <div 
                    className="w-16 h-16 rounded-xl bg-cover bg-center flex-shrink-0"
                    style={{ backgroundImage: `url(${program.cover_image})` }}
                  />
                  <div className="flex-1 min-w-0">
                    <h3 className="font-heading text-lg text-[#292524]">{program.title}</h3>
                    <p className="text-sm text-[#78716C]">
                      {program.modules?.length || 0} modules · {program.total_lessons} lessons
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => window.open(`/programs/${program.id}`, '_blank')}
                      className="p-2 hover:bg-[#F0EFEB] rounded-lg transition-colors"
                      title="Preview"
                    >
                      <ExternalLink size={18} className="text-[#78716C]" />
                    </button>
                    <button
                      onClick={() => navigate(`/admin/programs/${program.id}`)}
                      className="p-2 hover:bg-[#F0EFEB] rounded-lg transition-colors"
                      title="Edit"
                    >
                      <Pencil size={18} className="text-[#78716C]" />
                    </button>
                    <button
                      onClick={() => handleDeleteProgram(program.id)}
                      className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                      title="Delete"
                    >
                      <Trash2 size={18} className="text-red-500" />
                    </button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {showProgramForm && (
          <ProgramFormModal
            program={editingProgram}
            onClose={() => setShowProgramForm(false)}
            onSave={() => { setShowProgramForm(false); fetchPrograms(); }}
          />
        )}
      </div>
    </AdminLayout>
  );
};

const ProgramFormModal = ({ program, onClose, onSave }) => {
  const [title, setTitle] = useState(program?.title || "");
  const [description, setDescription] = useState(program?.description || "");
  const [coverImage, setCoverImage] = useState(program?.cover_image || "");
  const [estimatedHours, setEstimatedHours] = useState(program?.estimated_hours || 1);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (program) {
        await adminAPI.updateProgram(program.id, {
          title, description, cover_image: coverImage, estimated_hours: estimatedHours
        });
      } else {
        await adminAPI.createProgram({
          title, description, cover_image: coverImage, estimated_hours: estimatedHours
        });
      }
      onSave();
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-6 z-50">
      <Card className="max-w-md w-full p-6 space-y-4 max-h-[90vh] overflow-y-auto">
        <h3 className="font-heading text-xl text-[#292524]">
          {program ? "Edit Program" : "New Program"}
        </h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input label="Title" value={title} onChange={(e) => setTitle(e.target.value)} required />
          <Textarea label="Description" value={description} onChange={(e) => setDescription(e.target.value)} rows={3} />
          <Input label="Cover Image URL" value={coverImage} onChange={(e) => setCoverImage(e.target.value)} placeholder="https://..." />
          <Input label="Estimated Hours" type="number" value={estimatedHours} onChange={(e) => setEstimatedHours(parseInt(e.target.value))} min={1} />
          <div className="flex gap-3">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
            <Button type="submit" disabled={saving} className="flex-1">{saving ? "Saving..." : "Save"}</Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export const AdminProgramDetail = () => {
  const { programId } = useParams();
  const navigate = useNavigate();
  const [program, setProgram] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingModule, setEditingModule] = useState(null);
  const [showModuleForm, setShowModuleForm] = useState(false);
  const [editingLesson, setEditingLesson] = useState(null);
  const [showLessonForm, setShowLessonForm] = useState(false);
  const [selectedModuleId, setSelectedModuleId] = useState(null);

  const fetchProgram = useCallback(async () => {
    try {
      const res = await programsAPI.getById(programId);
      setProgram(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [programId]);

  useEffect(() => { fetchProgram(); }, [fetchProgram]);

  const handleDeleteModule = async (moduleId) => {
    if (!window.confirm("Delete this module and all its lessons?")) return;
    await adminAPI.deleteModule(moduleId);
    fetchProgram();
  };

  const handleDeleteLesson = async (lessonId) => {
    if (!window.confirm("Delete this lesson?")) return;
    await adminAPI.deleteLesson(lessonId);
    fetchProgram();
  };

  const handleMoveModule = async (moduleId, direction) => {
    const modules = [...program.modules];
    const idx = modules.findIndex(m => m.id === moduleId);
    if (direction === "up" && idx > 0) [modules[idx], modules[idx-1]] = [modules[idx-1], modules[idx]];
    else if (direction === "down" && idx < modules.length - 1) [modules[idx], modules[idx+1]] = [modules[idx+1], modules[idx]];
    else return;
    await adminAPI.reorderModules(modules.map(m => m.id));
    fetchProgram();
  };

  const handleMoveLesson = async (moduleId, lessonId, direction) => {
    const module = program.modules.find(m => m.id === moduleId);
    if (!module) return;
    const lessons = [...module.lessons];
    const idx = lessons.findIndex(l => l.id === lessonId);
    if (direction === "up" && idx > 0) [lessons[idx], lessons[idx-1]] = [lessons[idx-1], lessons[idx]];
    else if (direction === "down" && idx < lessons.length - 1) [lessons[idx], lessons[idx+1]] = [lessons[idx+1], lessons[idx]];
    else return;
    await adminAPI.reorderLessons(lessons.map(l => l.id));
    fetchProgram();
  };

  if (loading) return <AdminLayout><div className="text-center py-8 text-[#78716C]">Loading...</div></AdminLayout>;
  if (!program) return null;

  return (
    <AdminLayout>
      <div className="space-y-6">
        <button onClick={() => navigate("/admin/programs")} className="flex items-center gap-2 text-[#78716C] hover:text-[#4A6741]">
          <ArrowLeft size={18} /> Back to Programs
        </button>

        <div className="flex items-center gap-4">
          <div className="w-20 h-20 rounded-xl bg-cover bg-center" style={{ backgroundImage: `url(${program.cover_image})` }} />
          <div className="flex-1">
            <h2 className="font-heading text-2xl text-[#292524]">{program.title}</h2>
            <p className="text-[#78716C]">{program.modules?.length || 0} modules</p>
          </div>
          <Button variant="outline" onClick={() => window.open(`/programs/${program.id}`, '_blank')}>
            <ExternalLink size={18} /> Preview
          </Button>
        </div>

        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="font-heading text-xl text-[#292524]">Modules</h3>
            <Button onClick={() => { setEditingModule(null); setShowModuleForm(true); }}><Plus size={18} /> Add Module</Button>
          </div>

          {program.modules?.map((module, modIdx) => (
            <Card key={module.id} className="p-4 space-y-4">
              <div className="flex items-center gap-3">
                <div className="flex flex-col gap-1">
                  <button onClick={() => handleMoveModule(module.id, "up")} disabled={modIdx === 0} className="p-1 hover:bg-[#F0EFEB] rounded disabled:opacity-30"><ChevronUp size={16} /></button>
                  <button onClick={() => handleMoveModule(module.id, "down")} disabled={modIdx === program.modules.length - 1} className="p-1 hover:bg-[#F0EFEB] rounded disabled:opacity-30"><ChevronDown size={16} /></button>
                </div>
                <div className="flex-1">
                  <p className="text-xs text-[#78716C]">Module {modIdx + 1}</p>
                  <h4 className="font-heading text-lg text-[#292524]">{module.title}</h4>
                </div>
                <button onClick={() => { setEditingModule(module); setShowModuleForm(true); }} className="p-2 hover:bg-[#F0EFEB] rounded-lg"><Pencil size={18} className="text-[#78716C]" /></button>
                <button onClick={() => handleDeleteModule(module.id)} className="p-2 hover:bg-red-50 rounded-lg"><Trash2 size={18} className="text-red-500" /></button>
              </div>

              <div className="pl-8 space-y-2">
                {module.lessons?.map((lesson, lesIdx) => (
                  <div key={lesson.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-[#F0EFEB]">
                    <div className="flex flex-col gap-0.5">
                      <button onClick={() => handleMoveLesson(module.id, lesson.id, "up")} disabled={lesIdx === 0} className="p-0.5 hover:bg-white rounded disabled:opacity-30"><ChevronUp size={14} /></button>
                      <button onClick={() => handleMoveLesson(module.id, lesson.id, "down")} disabled={lesIdx === module.lessons.length - 1} className="p-0.5 hover:bg-white rounded disabled:opacity-30"><ChevronDown size={14} /></button>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[#292524] truncate">{lesson.title}</p>
                      <p className="text-xs text-[#78716C]">{lesson.estimated_minutes} min</p>
                    </div>
                    <button onClick={() => window.open(`/lesson/${lesson.id}?program=${programId}&module=${module.id}`, '_blank')} className="p-1.5 hover:bg-white rounded"><ExternalLink size={16} className="text-[#78716C]" /></button>
                    <button onClick={() => { setEditingLesson(lesson); setSelectedModuleId(module.id); setShowLessonForm(true); }} className="p-1.5 hover:bg-white rounded"><Pencil size={16} className="text-[#78716C]" /></button>
                    <button onClick={() => handleDeleteLesson(lesson.id)} className="p-1.5 hover:bg-red-50 rounded"><Trash2 size={16} className="text-red-500" /></button>
                  </div>
                ))}
                <button onClick={() => { setEditingLesson(null); setSelectedModuleId(module.id); setShowLessonForm(true); }} className="flex items-center gap-2 text-sm text-[#4A6741] hover:underline"><Plus size={16} /> Add Lesson</button>
              </div>
            </Card>
          ))}
        </div>

        {showModuleForm && <ModuleFormModal module={editingModule} programId={programId} onClose={() => setShowModuleForm(false)} onSave={() => { setShowModuleForm(false); fetchProgram(); }} />}
        {showLessonForm && <LessonFormModal lesson={editingLesson} moduleId={selectedModuleId} programId={programId} onClose={() => setShowLessonForm(false)} onSave={() => { setShowLessonForm(false); fetchProgram(); }} />}
      </div>
    </AdminLayout>
  );
};

const ModuleFormModal = ({ module, programId, onClose, onSave }) => {
  const [title, setTitle] = useState(module?.title || "");
  const [description, setDescription] = useState(module?.description || "");
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    if (module) await adminAPI.updateModule(module.id, { title, description });
    else await adminAPI.createModule({ program_id: programId, title, description });
    setSaving(false);
    onSave();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-6 z-50">
      <Card className="max-w-md w-full p-6 space-y-4">
        <h3 className="font-heading text-xl text-[#292524]">{module ? "Edit Module" : "New Module"}</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input label="Title" value={title} onChange={(e) => setTitle(e.target.value)} required />
          <Textarea label="Description" value={description} onChange={(e) => setDescription(e.target.value)} rows={3} />
          <div className="flex gap-3">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
            <Button type="submit" disabled={saving} className="flex-1">{saving ? "Saving..." : "Save"}</Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

const LessonFormModal = ({ lesson, moduleId, programId, onClose, onSave }) => {
  const [title, setTitle] = useState(lesson?.title || "");
  const [shortDescription, setShortDescription] = useState(lesson?.short_description || "");
  const [intentionText, setIntentionText] = useState(lesson?.intention_text || "");
  const [videoUrl, setVideoUrl] = useState(lesson?.video_url || "");
  const [practiceSteps, setPracticeSteps] = useState(lesson?.practice_steps?.join("\n") || "");
  const [reflectionPrompts, setReflectionPrompts] = useState(lesson?.reflection_prompts?.join("\n") || "");
  const [estimatedMinutes, setEstimatedMinutes] = useState(lesson?.estimated_minutes || 15);
  const [published, setPublished] = useState(lesson?.published ?? true);
  const [ttsEnabled, setTtsEnabled] = useState(lesson?.tts_enabled ?? true);
  const [ttsScript, setTtsScript] = useState(lesson?.tts_script || "");
  const [defaultAmbient, setDefaultAmbient] = useState(lesson?.default_ambient || "none");
  const [defaultVoiceVolume, setDefaultVoiceVolume] = useState(lesson?.default_voice_volume ?? 80);
  const [defaultAmbientVolume, setDefaultAmbientVolume] = useState(lesson?.default_ambient_volume ?? 30);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("content");

  const ambientOptions = [
    { value: "none", label: "None" },
    { value: "rain", label: "Rain" },
    { value: "forest", label: "Forest" },
    { value: "ocean", label: "Ocean Waves" },
    { value: "wind", label: "Wind" },
    { value: "chimes", label: "Chimes" },
    { value: "stream", label: "Stream" }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const data = {
      title, 
      short_description: shortDescription, 
      intention_text: intentionText, 
      video_url: videoUrl || null,
      practice_steps: practiceSteps.split("\n").filter(s => s.trim()),
      reflection_prompts: reflectionPrompts.split("\n").filter(s => s.trim()),
      estimated_minutes: estimatedMinutes,
      published,
      tts_enabled: ttsEnabled,
      tts_script: ttsScript || null,
      default_ambient: defaultAmbient,
      default_voice_volume: defaultVoiceVolume,
      default_ambient_volume: defaultAmbientVolume
    };
    if (lesson) await adminAPI.updateLesson(lesson.id, data);
    else await adminAPI.createLesson({ ...data, module_id: moduleId });
    setSaving(false);
    onSave();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-6 z-50 overflow-y-auto">
      <Card className="max-w-2xl w-full p-6 space-y-4 my-8 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h3 className="font-heading text-xl text-[#292524]">{lesson ? "Edit Lesson" : "New Lesson"}</h3>
          <div className="flex items-center gap-3">
            {lesson && (
              <button 
                onClick={() => window.open(`/lesson/${lesson.id}?program=${programId}&module=${moduleId}`, '_blank')} 
                className="text-sm text-[#4A6741] hover:underline flex items-center gap-1"
              >
                <ExternalLink size={14} /> Preview
              </button>
            )}
            <label className="flex items-center gap-2 cursor-pointer">
              <span className={`text-sm font-medium ${published ? 'text-[#4A6741]' : 'text-[#78716C]'}`}>
                {published ? 'Published' : 'Draft'}
              </span>
              <button
                type="button"
                onClick={() => setPublished(!published)}
                className={`w-10 h-6 rounded-full transition-colors ${published ? "bg-[#4A6741]" : "bg-[#E6E2DD]"}`}
                data-testid="publish-toggle"
              >
                <div className={`w-4 h-4 bg-white rounded-full shadow transition-transform ${published ? "translate-x-5" : "translate-x-1"}`} />
              </button>
            </label>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-[#E6E2DD]">
          {[
            { id: "content", label: "Content" },
            { id: "media", label: "Media & Audio" },
            { id: "settings", label: "Settings" }
          ].map(tab => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id 
                  ? "border-[#4A6741] text-[#4A6741]" 
                  : "border-transparent text-[#78716C] hover:text-[#292524]"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Content Tab */}
          {activeTab === "content" && (
            <div className="space-y-4">
              <Input label="Title" value={title} onChange={(e) => setTitle(e.target.value)} required data-testid="lesson-title" />
              <Textarea label="Short Description" value={shortDescription} onChange={(e) => setShortDescription(e.target.value)} rows={2} />
              <Textarea 
                label="Opening Intention (used for TTS meditation)" 
                value={intentionText} 
                onChange={(e) => setIntentionText(e.target.value)} 
                rows={3}
                data-testid="lesson-intention"
              />
              <Textarea 
                label="Practice Steps (one per line)" 
                value={practiceSteps} 
                onChange={(e) => setPracticeSteps(e.target.value)} 
                rows={5}
                placeholder="Step 1: Find a comfortable position...&#10;Step 2: Close your eyes gently..."
              />
              <Textarea 
                label="Reflection Prompts (one per line)" 
                value={reflectionPrompts} 
                onChange={(e) => setReflectionPrompts(e.target.value)} 
                rows={3}
                placeholder="What came up for you during this practice?&#10;What insights did you receive?"
              />
            </div>
          )}

          {/* Media & Audio Tab */}
          {activeTab === "media" && (
            <div className="space-y-6">
              {/* Video Section */}
              <div className="space-y-3">
                <h4 className="font-medium text-[#292524]">Video</h4>
                <Input 
                  label="Video URL" 
                  value={videoUrl} 
                  onChange={(e) => setVideoUrl(e.target.value)} 
                  placeholder="Paste embed URL (YouTube, Vimeo, Kajabi, or direct MP4)"
                  data-testid="lesson-video-url"
                />
                <p className="text-xs text-[#78716C]">
                  Supported formats: YouTube embed, Vimeo embed, Kajabi embed, direct .mp4 URL
                </p>
              </div>

              {/* TTS Meditation Section */}
              <div className="space-y-3 p-4 bg-[#F0EFEB] rounded-xl">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium text-[#292524]">Guided Meditation (AI Voice)</h4>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <span className="text-sm text-[#78716C]">{ttsEnabled ? 'Enabled' : 'Disabled'}</span>
                    <button
                      type="button"
                      onClick={() => setTtsEnabled(!ttsEnabled)}
                      className={`w-10 h-6 rounded-full transition-colors ${ttsEnabled ? "bg-[#4A6741]" : "bg-[#E6E2DD]"}`}
                      data-testid="tts-toggle"
                    >
                      <div className={`w-4 h-4 bg-white rounded-full shadow transition-transform ${ttsEnabled ? "translate-x-5" : "translate-x-1"}`} />
                    </button>
                  </label>
                </div>
                {ttsEnabled && (
                  <>
                    <Textarea 
                      label="TTS Script (optional - uses Opening Intention if empty)" 
                      value={ttsScript} 
                      onChange={(e) => setTtsScript(e.target.value)} 
                      rows={4}
                      placeholder="Custom meditation script for text-to-speech generation..."
                      data-testid="tts-script"
                    />
                    <p className="text-xs text-[#78716C]">
                      Leave empty to use the Opening Intention text. Audio is generated when user clicks "Generate" on the lesson page.
                    </p>
                  </>
                )}
              </div>

              {/* Default Ambient Sound */}
              <div className="space-y-3">
                <h4 className="font-medium text-[#292524]">Default Ambient Sound</h4>
                <select
                  value={defaultAmbient}
                  onChange={(e) => setDefaultAmbient(e.target.value)}
                  className="w-full h-12 px-4 rounded-xl border border-[#E6E2DD] bg-white text-[#292524] focus:outline-none focus:ring-2 focus:ring-[#4A6741]/20"
                  data-testid="default-ambient"
                >
                  {ambientOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              {/* Volume Defaults */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-[#292524]">
                    Default Voice Volume: {defaultVoiceVolume}%
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={defaultVoiceVolume}
                    onChange={(e) => setDefaultVoiceVolume(parseInt(e.target.value))}
                    className="w-full h-2 bg-[#E6E2DD] rounded-full appearance-none cursor-pointer accent-[#4A6741]"
                    data-testid="default-voice-volume"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-[#292524]">
                    Default Ambient Volume: {defaultAmbientVolume}%
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={defaultAmbientVolume}
                    onChange={(e) => setDefaultAmbientVolume(parseInt(e.target.value))}
                    className="w-full h-2 bg-[#E6E2DD] rounded-full appearance-none cursor-pointer accent-[#4A6741]"
                    data-testid="default-ambient-volume"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Settings Tab */}
          {activeTab === "settings" && (
            <div className="space-y-4">
              <Input 
                label="Estimated Minutes" 
                type="number" 
                value={estimatedMinutes} 
                onChange={(e) => setEstimatedMinutes(parseInt(e.target.value))} 
                min={1} 
              />
              <div className="p-4 bg-[#F0EFEB] rounded-xl space-y-3">
                <h4 className="font-medium text-[#292524]">Publishing</h4>
                <p className="text-sm text-[#78716C]">
                  {published 
                    ? "This lesson is visible to users with access to the program." 
                    : "This lesson is hidden from users. Only admins can see it."}
                </p>
                <Button 
                  type="button" 
                  variant={published ? "outline" : "default"}
                  onClick={() => setPublished(!published)}
                  className="w-full"
                >
                  {published ? "Unpublish Lesson" : "Publish Lesson"}
                </Button>
              </div>
            </div>
          )}

          <div className="flex gap-3 pt-4 border-t border-[#E6E2DD]">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
            <Button type="submit" disabled={saving} className="flex-1" data-testid="save-lesson-btn">
              {saving ? "Saving..." : "Save Lesson"}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

const ViewAsButton = ({ user }) => {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/admin/users/view-as`, { user_id: user.id });
      localStorage.setItem("reclaim_admin_token", localStorage.getItem("reclaim_token"));
      localStorage.setItem("reclaim_view_as", r.data.user.name || r.data.user.email);
      localStorage.setItem("reclaim_token", r.data.token);
      localStorage.removeItem("reclaim_user_cache");
      window.location.href = "/";
    } catch (e) { alert(e?.response?.data?.detail || "Couldn't open their view"); setBusy(false); }
  };
  return (
    <button onClick={go} disabled={busy} className="text-sm text-[#4A6741] hover:underline" data-testid={`view-as-btn-${user.id}`}
      title="See the app exactly as this member does (1 hour, logged)">{busy ? "…" : "View as"}</button>
  );
};

export const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user: currentUser } = useAuth();
  // Per-user Atelier credit balances, fetched lazily once on first load
  // for each user. Keyed by user.id → { credits_available, credits_used }.
  const [credits, setCredits] = useState({});
  // Which user-id is currently showing the grant-credits panel.
  const [grantingFor, setGrantingFor] = useState(null);
  const [grantAmount, setGrantAmount] = useState(5);
  const [grantBusy, setGrantBusy] = useState(false);
  const [grantMsg, setGrantMsg] = useState("");
  const [magicBusy, setMagicBusy] = useState(null);
  const [magicCopied, setMagicCopied] = useState(null);
  // Absorption pilot — one-tap copy of a ready-to-paste WhatsApp/
  // iMessage message that lands the user straight on /absorption.
  const [absorbBusy, setAbsorbBusy] = useState(null);
  const [absorbCopied, setAbsorbCopied] = useState(null);
  const [scopeBusy, setScopeBusy] = useState(null);
  const [scopeOptions, setScopeOptions] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem("reclaim_token");
        const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/users/access-scopes`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (r.ok) setScopeOptions((await r.json()).scopes || []);
      } catch (e) { /* non-fatal */ }
    })();
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await adminAPI.getUsers();
      setUsers(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  // Lazy-load credit balances for the visible users (one extra request
  // per user, but parallel — cheap, and only admins see this view).
  useEffect(() => {
    if (!users.length) return;
    const need = users.filter((u) => credits[u.id] === undefined);
    if (!need.length) return;
    need.forEach(async (u) => {
      try {
        const r = await adminAPI.getAtelierCredits(u.email || u.id);
        setCredits((prev) => ({
          ...prev,
          [u.id]: {
            available: r.data.credits_available ?? 0,
            used: r.data.credits_used ?? 0,
          },
        }));
      } catch (e) {
        setCredits((prev) => ({ ...prev, [u.id]: { available: 0, used: 0 } }));
      }
    });
  }, [users, credits]);

  const handleToggleAdmin = async (userId, makeAdmin) => {
    await adminAPI.toggleAdmin(userId, makeAdmin);
    fetchUsers();
  };

  const handleSetScope = async (userId, scope) => {
    setScopeBusy(userId);
    try {
      const token = localStorage.getItem("reclaim_token");
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/users/set-access-scope`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ user_id: userId, access_scope: scope || null }),
      });
      if (!r.ok) throw new Error(await r.text());
      await fetchUsers();
    } catch (e) {
      alert(`Failed to set scope: ${e.message}`);
    } finally {
      setScopeBusy(null);
    }
  };

  const handleMagicLink = async (u) => {
    setMagicBusy(u.id);
    try {
      const token = localStorage.getItem("reclaim_token");
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/magic-link/${u.id}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(await r.text());
      const data = await r.json();
      await navigator.clipboard.writeText(data.magic_url);
      setMagicCopied(u.id);
      setTimeout(() => setMagicCopied(null), 2200);
    } catch (e) {
      alert(`Magic link failed: ${e.message}`);
    } finally {
      setMagicBusy(null);
    }
  };

  // One-tap WhatsApp copy for the Absorption pilot. Mints a fresh
  // magic-link, appends ?next=/absorption so the user lands directly
  // on the upload screen, and copies a warm, ready-to-paste message.
  const handleAbsorptionCopy = async (u) => {
    setAbsorbBusy(u.id);
    try {
      const token = localStorage.getItem("reclaim_token");
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/magic-link/${u.id}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(await r.text());
      const data = await r.json();
      const url = `${data.magic_url}?next=/absorption`;
      const firstName = (u.name || u.email || "there").trim().split(/\s+/)[0];
      const message =
        `Hi ${firstName} — your Reclaim Absorption is ready.\n\n` +
        `Tap this one-time link, upload your PDFs, and we'll take care of the rest.\n\n${url}`;
      await navigator.clipboard.writeText(message);
      setAbsorbCopied(u.id);
      setTimeout(() => setAbsorbCopied(null), 2200);
    } catch (e) {
      alert(`Absorption link failed: ${e.message}`);
    } finally {
      setAbsorbBusy(null);
    }
  };

  const handleGrant = async (u) => {
    setGrantBusy(true);
    setGrantMsg("");
    try {
      const r = await adminAPI.grantAtelierCredits(u.email || u.id, grantAmount);
      setCredits((prev) => ({
        ...prev,
        [u.id]: {
          available: r.data.credits_available ?? 0,
          used: r.data.credits_used ?? 0,
        },
      }));
      setGrantMsg(`Granted ${grantAmount} credit${grantAmount === 1 ? '' : 's'} to ${u.name || u.email}.`);
      setTimeout(() => { setGrantMsg(""); setGrantingFor(null); }, 1800);
    } catch (e) {
      setGrantMsg(e?.response?.data?.detail || "Grant failed.");
    } finally {
      setGrantBusy(false);
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-4">
        <h2 className="font-heading text-xl text-[#292524]">Users</h2>
        {loading ? <div className="text-center py-8 text-[#78716C]">Loading...</div> : (
          <div className="space-y-3">
            {users.map((user) => {
              const userCred = credits[user.id];
              const showGrant = grantingFor === user.id;
              return (
              <Card key={user.id} className="p-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#4A6741] rounded-full flex items-center justify-center text-white font-heading text-lg">
                    {user.name?.charAt(0)?.toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-medium text-[#292524]">{user.name}</h3>
                      <MembershipBadge tier={user.membership_tier} />
                      {userCred !== undefined && (
                        <span
                          className="px-2 py-0.5 rounded-full text-[10px] font-medium tracking-wide"
                          style={{ background: '#F4EBD8', color: '#7A6126' }}
                          title={`${userCred.used} credits used`}
                          data-testid={`user-atelier-credits-${user.id}`}
                        >
                          ATELIER {userCred.available}
                        </span>
                      )}
                      {user.access_scope && (
                        <span
                          className="px-2 py-0.5 rounded-full text-[10px] font-medium tracking-wide"
                          style={{ background: '#EAF4E4', color: '#3E5D2E' }}
                          title={`Access scope: ${user.access_scope}`}
                          data-testid={`user-access-scope-${user.id}`}
                        >
                          SCOPE · {user.access_scope.replace(/_/g, ' ').toUpperCase()}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-[#78716C]">{user.email}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${user.role === "admin" ? "bg-[#4A6741]/10 text-[#4A6741]" : "bg-[#E6E2DD] text-[#78716C]"}`}>
                      {user.role}
                    </span>
                    {user.id !== currentUser?.id && (
                      <button onClick={() => handleToggleAdmin(user.id, user.role !== "admin")} className="text-sm text-[#4A6741] hover:underline">
                        {user.role === "admin" ? "Remove Admin" : "Make Admin"}
                      </button>
                    )}
                    {user.role !== "admin" && <ViewAsButton user={user} />}
                    <button
                      onClick={() => handleMagicLink(user)}
                      disabled={magicBusy === user.id}
                      className={`text-sm ${magicCopied === user.id ? "text-emerald-600" : "text-[#4A6741]"} hover:underline`}
                      data-testid={`magic-link-btn-${user.id}`}
                      title="Generate a 30-day one-tap sign-in link (copied to clipboard)"
                    >
                      {magicBusy === user.id
                        ? "…"
                        : magicCopied === user.id
                        ? "✓ Copied"
                        : "🔑 Magic link"}
                    </button>
                    <button
                      onClick={() => handleAbsorptionCopy(user)}
                      disabled={absorbBusy === user.id}
                      className={`text-sm ${absorbCopied === user.id ? "text-emerald-600" : "text-[#7A6126]"} hover:underline`}
                      data-testid={`absorption-copy-btn-${user.id}`}
                      title="Copy a ready-to-paste WhatsApp/iMessage message that lands them on /absorption"
                    >
                      {absorbBusy === user.id
                        ? "…"
                        : absorbCopied === user.id
                        ? "✓ Message copied"
                        : "🎧 Absorption link"}
                    </button>
                    <button
                      onClick={() => {
                        setGrantingFor(showGrant ? null : user.id);
                        setGrantAmount(5);
                        setGrantMsg("");
                      }}
                      className="text-sm text-[#7A6126] hover:underline"
                      data-testid={`grant-atelier-credits-toggle-${user.id}`}
                    >
                      {showGrant ? 'Cancel' : '+ Atelier'}
                    </button>
                    {scopeOptions.length > 0 && (
                      <select
                        value={user.access_scope || ""}
                        onChange={(e) => handleSetScope(user.id, e.target.value)}
                        disabled={scopeBusy === user.id}
                        className="text-xs bg-white border border-[#D7BB7A] rounded px-2 py-1 text-[#3E5D2E]"
                        title="Scope this user's access to a curated set of areas"
                        data-testid={`user-scope-select-${user.id}`}
                      >
                        {scopeOptions.map((s) => (
                          <option key={s.key || "full"} value={s.key || ""}>
                            {s.label}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                </div>
                {showGrant && (
                  <div
                    className="mt-3 ml-16 pl-2 flex items-center gap-3 flex-wrap"
                    data-testid={`grant-atelier-credits-panel-${user.id}`}
                  >
                    <span className="text-xs text-[#78716C]">Grant</span>
                    {[1, 5, 10].map((n) => (
                      <button
                        key={n}
                        onClick={() => setGrantAmount(n)}
                        disabled={grantBusy}
                        className={`px-3 py-1 rounded-full text-xs font-medium border ${grantAmount === n ? 'bg-[#7A6126] text-white border-[#7A6126]' : 'bg-white text-[#7A6126] border-[#D7BB7A]'}`}
                        data-testid={`grant-atelier-credits-amount-${n}-${user.id}`}
                      >
                        {n}
                      </button>
                    ))}
                    <span className="text-xs text-[#78716C]">credit{grantAmount === 1 ? '' : 's'}</span>
                    <button
                      onClick={() => handleGrant(user)}
                      disabled={grantBusy}
                      className="px-3 py-1 rounded-full text-xs font-medium bg-[#4A6741] text-white disabled:opacity-50"
                      data-testid={`grant-atelier-credits-confirm-${user.id}`}
                    >
                      {grantBusy ? 'Granting…' : 'Grant'}
                    </button>
                    {grantMsg && (
                      <span
                        className="text-xs"
                        style={{ color: grantMsg.includes('Granted') ? '#4A6741' : '#B7410E' }}
                        data-testid={`grant-atelier-credits-msg-${user.id}`}
                      >
                        {grantMsg}
                      </span>
                    )}
                  </div>
                )}
              </Card>
              );
            })}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};


export const AdminAffiliates = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "reclaim2026",
    custom_referral_code: "",
    grant_integration_program: true,
  });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [recent, setRecent] = useState([]);
  const [copied, setCopied] = useState("");

  const reset = () => {
    setForm({
      name: "",
      email: "",
      password: "reclaim2026",
      custom_referral_code: "",
      grant_integration_program: true,
    });
    setResult(null);
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setResult(null);
    if (!form.name.trim() || !form.email.trim() || !form.password.trim()) {
      setError("Name, email and password are required.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        password: form.password,
        grant_integration_program: form.grant_integration_program,
      };
      if (form.custom_referral_code.trim()) {
        payload.custom_referral_code = form.custom_referral_code.trim().toUpperCase();
      }
      const res = await adminAPI.spawnAffiliate(payload);
      setResult({ ...res.data, _password: form.password });
      setRecent((prev) => [{ ...res.data, _password: form.password, ts: Date.now() }, ...prev].slice(0, 5));
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message || "Failed to spawn account";
      setError(typeof detail === "string" ? detail : JSON.stringify(detail));
    } finally {
      setSubmitting(false);
    }
  };

  const copy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied(""), 1500);
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-5">
        <div>
          <h2 className="font-heading text-xl text-[#292524]">Affiliate Spawner</h2>
          <p className="text-sm text-[#78716C]">
            Create a full-access premium affiliate account in one click. Mirrors the Oscar setup:
            premium tier, integration program unlocked, referral code auto-issued.
          </p>
        </div>

        <Card className="p-5 space-y-4" data-testid="admin-affiliate-spawn-card">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs uppercase tracking-wider text-[#78716C] mb-1.5">Full name</label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Jane Doe"
                  data-testid="affiliate-spawn-name"
                  required
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-[#78716C] mb-1.5">Email</label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="jane@reclaim.app"
                  data-testid="affiliate-spawn-email"
                  required
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-[#78716C] mb-1.5">Initial password</label>
                <Input
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder="reclaim2026"
                  data-testid="affiliate-spawn-password"
                  required
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-[#78716C] mb-1.5">
                  Custom referral code <span className="text-[#9CA3AF] normal-case">(optional)</span>
                </label>
                <Input
                  value={form.custom_referral_code}
                  onChange={(e) =>
                    setForm({ ...form, custom_referral_code: e.target.value.toUpperCase() })
                  }
                  placeholder="Auto-generated if blank"
                  data-testid="affiliate-spawn-ref"
                />
              </div>
            </div>

            <label className="flex items-center gap-3 text-sm text-[#292524]">
              <input
                type="checkbox"
                checked={form.grant_integration_program}
                onChange={(e) => setForm({ ...form, grant_integration_program: e.target.checked })}
                className="w-4 h-4 accent-[#4A6741]"
                data-testid="affiliate-spawn-grant-integration"
              />
              Grant Integration Program access
            </label>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-3 py-2" data-testid="affiliate-spawn-error">
                {error}
              </div>
            )}

            <div className="flex gap-3">
              <Button type="submit" disabled={submitting} className="flex-1" data-testid="affiliate-spawn-submit">
                {submitting ? "Creating…" : (<><UserPlus size={16} /> Spawn affiliate</>)}
              </Button>
              {(result || error) && (
                <Button type="button" variant="outline" onClick={reset} data-testid="affiliate-spawn-reset">
                  Clear
                </Button>
              )}
            </div>
          </form>

          {result && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-4 rounded-xl border border-[#4A6741]/30 bg-[#4A6741]/5 p-4 space-y-3"
              data-testid="affiliate-spawn-result"
            >
              <div className="flex items-center gap-2 text-[#4A6741] font-medium">
                <Check size={18} />
                {result.created ? "Account created" : "Account upgraded to full-access"}
              </div>
              <div className="grid sm:grid-cols-2 gap-y-2 gap-x-4 text-sm">
                <CopyRow label="Name" value={result.user.name} k="name" copied={copied} onCopy={copy} />
                <CopyRow label="Email" value={result.user.email} k="email" copied={copied} onCopy={copy} />
                {result.password_set && (
                  <CopyRow label="Password" value={result._password} k="pw" copied={copied} onCopy={copy} />
                )}
                <CopyRow label="Referral code" value={result.referral_code} k="ref" copied={copied} onCopy={copy} />
              </div>
              <div className="text-xs text-[#78716C]">
                Tier: <strong className="text-[#292524]">premium</strong> · Role: <strong className="text-[#292524]">user</strong>
                {result.integration_program_access && <> · Integration Program: <strong className="text-[#4A6741]">unlocked</strong></>}
              </div>
            </motion.div>
          )}
        </Card>

        {recent.length > 0 && (
          <Card className="p-5 space-y-3">
            <h3 className="font-medium text-[#292524]">Recently spawned (this session)</h3>
            <div className="divide-y divide-[#E6E2DD]">
              {recent.map((r) => (
                <div key={`${r.user.email}-${r.ts}`} className="py-2.5 flex items-center justify-between text-sm">
                  <div className="min-w-0">
                    <div className="font-medium text-[#292524] truncate">{r.user.name}</div>
                    <div className="text-xs text-[#78716C] truncate">{r.user.email} · {r.referral_code}</div>
                  </div>
                  <span className="text-xs text-[#4A6741]">{r.created ? "new" : "upgraded"}</span>
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>
    </AdminLayout>
  );
};

const CopyRow = ({ label, value, k, copied, onCopy }) => (
  <div className="flex items-center justify-between gap-2">
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-wider text-[#78716C]">{label}</div>
      <div className="text-[#292524] font-mono text-sm truncate">{value}</div>
    </div>
    <button
      type="button"
      onClick={() => onCopy(value, k)}
      className="text-xs text-[#4A6741] hover:underline flex items-center gap-1 shrink-0"
      data-testid={`affiliate-copy-${k}`}
    >
      {copied === k ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy</>}
    </button>
  </div>
);


export const AdminImages = () => {
  const [programs, setPrograms] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categoryImages, setCategoryImages] = useState([]);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [programsRes, categoriesRes] = await Promise.all([
        programsAPI.getAll(),
        imageLibraryAPI.getCategories()
      ]);
      setPrograms(programsRes.data);
      setCategories(categoriesRes.data);
      if (categoriesRes.data?.length > 0) {
        setSelectedCategory(categoriesRes.data[0].id);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (selectedCategory) {
      fetchCategoryImages(selectedCategory);
    }
  }, [selectedCategory]);

  const fetchCategoryImages = async (categoryId) => {
    try {
      const res = await imageLibraryAPI.getCategory(categoryId);
      setCategoryImages(res.data?.images || []);
    } catch (e) {
      console.error(e);
      setCategoryImages([]);
    }
  };

  const handleImageSelect = async (imageUrl) => {
    if (!selectedProgram) return;
    
    setSaving(true);
    try {
      await adminAPI.updateProgram(selectedProgram.id, {
        cover_image: imageUrl
      });
      // Update local state
      setPrograms(prev => prev.map(p => 
        p.id === selectedProgram.id ? { ...p, cover_image: imageUrl } : p
      ));
      setSelectedProgram(prev => ({ ...prev, cover_image: imageUrl }));
    } catch (e) {
      console.error(e);
      alert("Failed to update image");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="text-center py-8 text-[#78716C]">Loading...</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h2 className="font-heading text-xl text-[#292524]">Program Images</h2>
          <p className="text-sm text-[#78716C]">Select a program, then choose an image from the library</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Programs List */}
          <div className="space-y-4">
            <h3 className="font-medium text-[#292524]">Programs</h3>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {programs.map((program) => (
                <Card
                  key={program.id}
                  className={`p-3 cursor-pointer transition-all ${
                    selectedProgram?.id === program.id 
                      ? 'ring-2 ring-[#4A6741] bg-[#4A6741]/5' 
                      : 'hover:bg-[#F0EFEB]'
                  }`}
                  onClick={() => setSelectedProgram(program)}
                  data-testid={`program-select-${program.id}`}
                >
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-14 h-14 rounded-lg bg-cover bg-center flex-shrink-0 border border-[#E6E2DD]"
                      style={{ backgroundImage: `url(${program.cover_image})` }}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-[#292524] truncate">{program.title}</p>
                      <p className="text-xs text-[#78716C] truncate">{program.category}</p>
                    </div>
                    {selectedProgram?.id === program.id && (
                      <Check size={18} className="text-[#4A6741]" />
                    )}
                  </div>
                </Card>
              ))}
            </div>
          </div>

          {/* Image Library */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-medium text-[#292524]">Image Library</h3>
              {saving && (
                <span className="text-sm text-[#4A6741] flex items-center gap-2">
                  <div className="w-3 h-3 border-2 border-[#4A6741] border-t-transparent rounded-full animate-spin" />
                  Saving...
                </span>
              )}
            </div>

            {/* Category Tabs */}
            <div className="flex flex-wrap gap-2">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.id)}
                  className={`px-3 py-1.5 text-sm rounded-full transition-colors ${
                    selectedCategory === cat.id
                      ? 'bg-[#4A6741] text-white'
                      : 'bg-[#F0EFEB] text-[#78716C] hover:bg-[#E6E2DD]'
                  }`}
                  data-testid={`category-tab-${cat.id}`}
                >
                  {cat.name}
                </button>
              ))}
            </div>

            {/* Images Grid */}
            <div className="grid grid-cols-3 gap-2 max-h-80 overflow-y-auto p-1">
              {categoryImages.map((image) => (
                <button
                  key={image.id}
                  onClick={() => handleImageSelect(image.url)}
                  disabled={!selectedProgram || saving}
                  className={`aspect-square rounded-lg bg-cover bg-center border-2 transition-all ${
                    selectedProgram?.cover_image === image.url
                      ? 'border-[#4A6741] ring-2 ring-[#4A6741]/20'
                      : 'border-transparent hover:border-[#4A6741]/50'
                  } ${!selectedProgram ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                  style={{ backgroundImage: `url(${image.url})` }}
                  title={image.description}
                  data-testid={`image-select-${image.id}`}
                />
              ))}
            </div>

            {!selectedProgram && (
              <p className="text-sm text-[#78716C] text-center py-4 bg-[#F0EFEB] rounded-lg">
                Select a program to change its image
              </p>
            )}
          </div>
        </div>

        {/* Selected Program Preview */}
        {selectedProgram && (
          <Card className="p-4 bg-[#F0EFEB]">
            <div className="flex items-center gap-4">
              <div 
                className="w-24 h-24 rounded-xl bg-cover bg-center border border-[#E6E2DD]"
                style={{ backgroundImage: `url(${selectedProgram.cover_image})` }}
              />
              <div>
                <p className="text-xs uppercase tracking-wider text-[#78716C] mb-1">Current Selection</p>
                <h4 className="font-heading text-lg text-[#292524]">{selectedProgram.title}</h4>
                <p className="text-sm text-[#78716C] mt-1">
                  Click any image in the library to update
                </p>
              </div>
            </div>
          </Card>
        )}
      </div>
    </AdminLayout>
  );
};


// Challenge categories for dropdown
const CHALLENGE_CATEGORIES = [
  { id: "personal_power", name: "Personal Power" },
  { id: "health_energy", name: "Health & Energy" },
  { id: "mind_mastery", name: "Mind Mastery" },
  { id: "courage", name: "Courage" },
  { id: "awareness", name: "Awareness" },
  { id: "discipline", name: "Discipline" },
  { id: "emotional_intelligence", name: "Emotional Intelligence" },
  { id: "presence_practices", name: "Presence Practices" }
];

const DURATION_OPTIONS = [3, 7, 14, 21, 30];

export const AdminChallenges = () => {
  const [challenges, setChallenges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [showEditor, setShowEditor] = useState(false);
  const [editingChallenge, setEditingChallenge] = useState(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    category: "personal_power",
    duration: 7,
    image_url: "",
    video_url: "",
    daily_steps: [],
    affiliate_product: null,
    active: true,
    sort_order: 0
  });

  const fetchChallenges = useCallback(async () => {
    setLoading(true);
    try {
      const res = await challengesAPI.adminGetAll(categoryFilter, searchQuery);
      setChallenges(res.data.challenges || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [categoryFilter, searchQuery]);

  useEffect(() => {
    fetchChallenges();
  }, [fetchChallenges]);

  const handleCreate = () => {
    setEditingChallenge(null);
    setFormData({
      title: "",
      description: "",
      category: "personal_power",
      duration: 7,
      image_url: "",
      video_url: "",
      daily_steps: [],
      affiliate_product: null,
      active: true,
      sort_order: challenges.length
    });
    setShowEditor(true);
  };

  const handleEdit = (challenge) => {
    setEditingChallenge(challenge);
    setFormData({
      title: challenge.title,
      description: challenge.description,
      category: challenge.category,
      duration: challenge.duration,
      image_url: challenge.image_url || "",
      video_url: challenge.video_url || "",
      daily_steps: challenge.daily_steps || [],
      affiliate_product: challenge.affiliate_product || null,
      active: challenge.active,
      sort_order: challenge.sort_order || 0
    });
    setShowEditor(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editingChallenge) {
        await challengesAPI.adminUpdate(editingChallenge.id, formData);
      } else {
        await challengesAPI.adminCreate(formData);
      }
      setShowEditor(false);
      fetchChallenges();
    } catch (e) {
      console.error(e);
      alert("Failed to save challenge");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this challenge?")) return;
    try {
      await challengesAPI.adminDelete(id);
      fetchChallenges();
    } catch (e) {
      console.error(e);
      alert("Failed to delete challenge");
    }
  };

  const handleToggleActive = async (id) => {
    try {
      await challengesAPI.adminToggleActive(id);
      fetchChallenges();
    } catch (e) {
      console.error(e);
    }
  };

  const handleAddStep = () => {
    const newDay = formData.daily_steps.length + 1;
    setFormData({
      ...formData,
      daily_steps: [
        ...formData.daily_steps,
        { day: newDay, title: "", description: "", reflection_prompt: "" }
      ]
    });
  };

  const handleUpdateStep = (index, field, value) => {
    const steps = [...formData.daily_steps];
    steps[index] = { ...steps[index], [field]: value };
    setFormData({ ...formData, daily_steps: steps });
  };

  const handleRemoveStep = (index) => {
    const steps = formData.daily_steps.filter((_, i) => i !== index);
    // Renumber days
    const renumbered = steps.map((step, i) => ({ ...step, day: i + 1 }));
    setFormData({ ...formData, daily_steps: renumbered });
  };

  const handleAutoGenerateSteps = () => {
    const steps = [];
    for (let i = 1; i <= formData.duration; i++) {
      steps.push({
        day: i,
        title: `Day ${i}`,
        description: "",
        reflection_prompt: ""
      });
    }
    setFormData({ ...formData, daily_steps: steps });
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-heading text-xl text-[#292524]">Challenge Library</h2>
            <p className="text-sm text-[#78716C]">{challenges.length} challenges</p>
          </div>
          <Button onClick={handleCreate} data-testid="create-challenge-btn">
            <Plus size={18} /> New Challenge
          </Button>
        </div>

        {/* Filters */}
        <div className="flex gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" size={18} />
            <input
              type="text"
              placeholder="Search challenges..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-[#E6E2DD] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#4A6741]/20"
              data-testid="search-challenges-input"
            />
          </div>
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="px-4 py-2.5 border border-[#E6E2DD] rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#4A6741]/20"
            data-testid="category-filter-select"
          >
            <option value="">All Categories</option>
            {CHALLENGE_CATEGORIES.map(cat => (
              <option key={cat.id} value={cat.id}>{cat.name}</option>
            ))}
          </select>
        </div>

        {/* Challenge List */}
        {loading ? (
          <div className="text-center py-8 text-[#78716C]">Loading...</div>
        ) : (
          <div className="space-y-3">
            {challenges.map((challenge) => (
              <Card
                key={challenge.id}
                className={`p-4 ${!challenge.active ? 'opacity-60' : ''}`}
              >
                <div className="flex items-start gap-4">
                  {/* Image Preview */}
                  <div className="w-20 h-20 rounded-lg bg-gradient-to-br from-[#4A6741] to-[#3D5636] flex-shrink-0 overflow-hidden">
                    {challenge.image_url ? (
                      <img src={challenge.image_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Trophy className="text-white/40" size={24} />
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <h3 className="font-medium text-[#292524] truncate">{challenge.title}</h3>
                        <p className="text-xs text-[#78716C] mt-0.5">
                          {CHALLENGE_CATEGORIES.find(c => c.id === challenge.category)?.name || challenge.category}
                          {" • "}{challenge.duration} days
                          {" • "}{challenge.daily_steps?.length || 0} steps
                          {challenge.video_url && " • Has video"}
                        </p>
                      </div>
                      <button
                        onClick={() => handleToggleActive(challenge.id)}
                        className={`p-1 rounded ${challenge.active ? 'text-[#4A6741]' : 'text-[#9CA3AF]'}`}
                        title={challenge.active ? "Active" : "Inactive"}
                      >
                        {challenge.active ? <ToggleRight size={24} /> : <ToggleLeft size={24} />}
                      </button>
                    </div>
                    <p className="text-sm text-[#78716C] line-clamp-1 mt-1">{challenge.description}</p>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleEdit(challenge)}
                      className="p-2 text-[#78716C] hover:text-[#292524] hover:bg-[#F0EFEB] rounded-lg"
                      data-testid={`edit-challenge-${challenge.id}`}
                    >
                      <Pencil size={18} />
                    </button>
                    <button
                      onClick={() => handleDelete(challenge.id)}
                      className="p-2 text-[#78716C] hover:text-red-500 hover:bg-red-50 rounded-lg"
                      data-testid={`delete-challenge-${challenge.id}`}
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              </Card>
            ))}

            {challenges.length === 0 && (
              <div className="text-center py-12 text-[#78716C]">
                <Trophy className="mx-auto mb-4 text-[#D4A373]" size={48} />
                <p>No challenges found</p>
                <Button onClick={handleCreate} className="mt-4">
                  Create Your First Challenge
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Editor Modal */}
      {showEditor && (
        <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl w-full max-w-2xl my-8 shadow-2xl">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-5 border-b border-[#E6E2DD]">
              <h3 className="font-heading text-xl text-[#292524]">
                {editingChallenge ? "Edit Challenge" : "Create Challenge"}
              </h3>
              <button
                onClick={() => setShowEditor(false)}
                className="p-2 hover:bg-[#F0EFEB] rounded-lg"
              >
                <X size={20} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-5 space-y-5 max-h-[70vh] overflow-y-auto">
              {/* Basic Info */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#292524] mb-1">Title</label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Challenge title"
                    data-testid="challenge-title-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#292524] mb-1">Description</label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Challenge description"
                    rows={3}
                    data-testid="challenge-description-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#292524] mb-1">Category</label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      className="w-full px-4 py-2.5 border border-[#E6E2DD] rounded-xl text-sm bg-white"
                      data-testid="challenge-category-select"
                    >
                      {CHALLENGE_CATEGORIES.map(cat => (
                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#292524] mb-1">Duration</label>
                    <select
                      value={formData.duration}
                      onChange={(e) => setFormData({ ...formData, duration: parseInt(e.target.value) })}
                      className="w-full px-4 py-2.5 border border-[#E6E2DD] rounded-xl text-sm bg-white"
                      data-testid="challenge-duration-select"
                    >
                      {DURATION_OPTIONS.map(d => (
                        <option key={d} value={d}>{d} days</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#292524] mb-1">Image URL (optional)</label>
                  <Input
                    value={formData.image_url}
                    onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                    placeholder="https://..."
                    data-testid="challenge-image-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#292524] mb-1">Video URL (optional)</label>
                  <Input
                    value={formData.video_url}
                    onChange={(e) => setFormData({ ...formData, video_url: e.target.value })}
                    placeholder="YouTube, Vimeo, or direct video URL"
                    data-testid="challenge-video-input"
                  />
                  <p className="text-xs text-[#9CA3AF] mt-1">Supports YouTube, Vimeo, or direct video URLs</p>
                </div>

                {/* Affiliate Product Section */}
                <div className="border-t border-[#E6E2DD] pt-4 mt-4">
                  <div className="flex items-center justify-between mb-3">
                    <label className="text-sm font-medium text-[#292524]">Affiliate Product (optional)</label>
                    {!formData.affiliate_product ? (
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => setFormData({ 
                          ...formData, 
                          affiliate_product: { title: "", description: "", button_text: "Learn More", url: "", image_url: "" }
                        })}
                      >
                        <Plus size={16} /> Add Product
                      </Button>
                    ) : (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => setFormData({ ...formData, affiliate_product: null })}
                        className="text-red-500 hover:text-red-600"
                      >
                        <X size={16} /> Remove
                      </Button>
                    )}
                  </div>
                  
                  {formData.affiliate_product && (
                    <Card className="p-4 bg-[#FDF8F3] border-[#D4A373]/30">
                      <div className="space-y-3">
                        <Input
                          value={formData.affiliate_product.title}
                          onChange={(e) => setFormData({ 
                            ...formData, 
                            affiliate_product: { ...formData.affiliate_product, title: e.target.value }
                          })}
                          placeholder="Product title (e.g., 'Learn Spanish in 3 Weeks')"
                          data-testid="affiliate-title-input"
                        />
                        <Textarea
                          value={formData.affiliate_product.description || ""}
                          onChange={(e) => setFormData({ 
                            ...formData, 
                            affiliate_product: { ...formData.affiliate_product, description: e.target.value }
                          })}
                          placeholder="Short description (optional)"
                          rows={2}
                        />
                        <div className="grid grid-cols-2 gap-3">
                          <Input
                            value={formData.affiliate_product.button_text}
                            onChange={(e) => setFormData({ 
                              ...formData, 
                              affiliate_product: { ...formData.affiliate_product, button_text: e.target.value }
                            })}
                            placeholder="Button text"
                          />
                          <Input
                            value={formData.affiliate_product.image_url || ""}
                            onChange={(e) => setFormData({ 
                              ...formData, 
                              affiliate_product: { ...formData.affiliate_product, image_url: e.target.value }
                            })}
                            placeholder="Image URL (optional)"
                          />
                        </div>
                        <Input
                          value={formData.affiliate_product.url}
                          onChange={(e) => setFormData({ 
                            ...formData, 
                            affiliate_product: { ...formData.affiliate_product, url: e.target.value }
                          })}
                          placeholder="Affiliate URL (https://...)"
                          data-testid="affiliate-url-input"
                        />
                      </div>
                    </Card>
                  )}
                </div>
              </div>

              {/* Daily Steps */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-medium text-[#292524]">
                    Daily Steps ({formData.daily_steps.length})
                  </label>
                  <div className="flex gap-2">
                    {formData.daily_steps.length === 0 && (
                      <Button variant="outline" size="sm" onClick={handleAutoGenerateSteps}>
                        Auto-generate {formData.duration} days
                      </Button>
                    )}
                    <Button variant="outline" size="sm" onClick={handleAddStep}>
                      <Plus size={16} /> Add Day
                    </Button>
                  </div>
                </div>

                <div className="space-y-3 max-h-64 overflow-y-auto pr-2">
                  {formData.daily_steps.map((step, index) => (
                    <Card key={index} className="p-3 bg-[#F9FAFB]">
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 bg-[#4A6741] rounded-full flex items-center justify-center text-white text-sm flex-shrink-0">
                          {step.day}
                        </div>
                        <div className="flex-1 space-y-2">
                          <Input
                            value={step.title}
                            onChange={(e) => handleUpdateStep(index, "title", e.target.value)}
                            placeholder="Step title"
                            className="text-sm"
                          />
                          <Textarea
                            value={step.description}
                            onChange={(e) => handleUpdateStep(index, "description", e.target.value)}
                            placeholder="Step description"
                            rows={2}
                            className="text-sm"
                          />
                          <Input
                            value={step.reflection_prompt || ""}
                            onChange={(e) => handleUpdateStep(index, "reflection_prompt", e.target.value)}
                            placeholder="Reflection prompt (optional)"
                            className="text-sm"
                          />
                        </div>
                        <button
                          onClick={() => handleRemoveStep(index)}
                          className="p-1 text-[#9CA3AF] hover:text-red-500"
                        >
                          <X size={18} />
                        </button>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end gap-3 p-5 border-t border-[#E6E2DD]">
              <Button variant="outline" onClick={() => setShowEditor(false)}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving || !formData.title} data-testid="save-challenge-btn">
                {saving ? "Saving..." : editingChallenge ? "Update Challenge" : "Create Challenge"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};
